import { Mail, MessageCircle, Instagram, Send, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "../../lib/LangContext";
import { CONTACT_INFO, buildWhatsAppLink } from "../../lib/translations";
import { p22Copy } from "../../lib/p22Copy";
import { FooterCities } from "./FooterCities";
import { useLocalePath } from "./LangRouting";

export const Footer = () => {
  const { t, lang } = useLang();
  const lp = useLocalePath();
  const year = new Date().getFullYear();

  return (
    <footer
      data-testid="footer"
      className="relative bg-black border-t border-white/10 pt-20 pb-10"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 pb-16">
          <div className="md:col-span-1">
            <div className="font-heading text-white text-2xl tracking-tighter">
              PURE<span className="text-gold">GLOSS</span>
            </div>
            <div className="text-[10px] tracking-[0.32em] uppercase text-zinc-500 mt-2">
              {t.footer.tagline}
            </div>
            <p
              className="mt-6 inline-flex items-center gap-2 text-zinc-300 text-sm"
              data-testid="footer-based-in"
            >
              <MapPin size={14} className="text-gold" />
              {t.basedIn}
            </p>
          </div>

          <div>
            <div className="label-eyebrow mb-5">{t.contact.eyebrow}</div>
            <ul className="space-y-3 text-sm">
              <li>
                <a
                  data-testid="footer-whatsapp"
                  href={buildWhatsAppLink("Hi PUREGLOSS! ")}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-3 text-zinc-300 hover:text-gold transition-colors"
                >
                  <MessageCircle size={14} /> {CONTACT_INFO.whatsappDisplay}
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-email"
                  href={`mailto:${CONTACT_INFO.email}`}
                  className="inline-flex items-center gap-3 text-zinc-300 hover:text-gold transition-colors break-all"
                >
                  <Mail size={14} /> {CONTACT_INFO.email}
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-instagram"
                  href={CONTACT_INFO.instagramUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-3 text-zinc-300 hover:text-gold transition-colors"
                >
                  <Instagram size={14} /> {CONTACT_INFO.instagram}
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-telegram"
                  href={CONTACT_INFO.telegramUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-3 text-zinc-300 hover:text-gold transition-colors"
                >
                  <Send size={14} /> {CONTACT_INFO.telegram}
                </a>
              </li>
            </ul>
          </div>

          <div>
            <div className="label-eyebrow mb-5">{t.footer.area}</div>
            <p data-testid="footer-area-text" className="text-sm text-zinc-400 leading-relaxed">
              {p22Copy(lang).area.short}
            </p>
          </div>

          <div>
            <div className="label-eyebrow mb-5">{t.footer.legal}</div>
            <ul className="space-y-3 text-sm">
              <li>
                <Link
                  data-testid="footer-impressum"
                  to={lp("/impressum")}
                  className="text-zinc-300 hover:text-gold transition-colors"
                >
                  {t.footer.impressum}
                </Link>
              </li>
              <li>
                <Link
                  data-testid="footer-datenschutz"
                  to={lp("/datenschutz")}
                  className="text-zinc-300 hover:text-gold transition-colors"
                >
                  {t.footer.datenschutz}
                </Link>
              </li>
              <li>
                <Link
                  data-testid="footer-agb"
                  to={lp("/agb")}
                  className="text-zinc-300 hover:text-gold transition-colors"
                >
                  {t.footer.agb}
                </Link>
              </li>
              <li>
                <Link
                  data-testid="footer-widerruf"
                  to={lp("/widerruf")}
                  className="text-zinc-300 hover:text-gold transition-colors"
                >
                  {t.footer.widerruf}
                </Link>
              </li>
              <li>
                <Link
                  data-testid="footer-cookies"
                  to={lp("/cookies")}
                  className="text-zinc-300 hover:text-gold transition-colors"
                >
                  {t.footer.cookies}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <FooterCities />
        <div className="divider-hairline" />

        <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8 text-[11px] tracking-[0.2em] uppercase text-zinc-500">
          <div>© {year} PUREGLOSS. {t.footer.rights}</div>
          <div>{t.footer.madeIn}</div>
        </div>
      </div>
    </footer>
  );
};
